import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { viewTutorDetail } from '../../slice/tutorSlice'
import moment from 'moment'
import { hasPermission } from '../../common/permissions'

const ViewTutorDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const tutorId = location.state?.tutorId
    const { tutorDetail, loading } = useSelector(state => state.tutor)

    const [activeTab, setActiveTab] = useState('personal')
    const [selectedImage, setSelectedImage] = useState(null)

    useEffect(() => {
        if (!tutorId) { navigate('/tutors'); return }
        dispatch(viewTutorDetail({ id: tutorId }))
    }, [tutorId, dispatch, navigate])

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (!tutorDetail) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="alert alert-warning">Tutor not found</div>
            </div>
        )
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getStatusBadge = (status) => {
        const map = {
            pending: { bg: 'bg-warning', text: 'Pending' },
            approved: { bg: 'bg-success', text: 'Approved' },
            rejected: { bg: 'bg-danger', text: 'Rejected' },
        }
        const c = map[status] || { bg: 'bg-secondary', text: status }
        return <span className={`badge ${c.bg}`}>{c.text}</span>
    }

    const getGenderBadge = (gender) =>
        gender === 'male' ? <span className="badge bg-primary">Male</span> :
            gender === 'female' ? <span className="badge bg-info">Female</span> :
                <span className="badge bg-secondary">Other</span>

    const ImageModal = ({ image, onClose }) => {
        if (!image) return null
        return (
            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} onClick={onClose}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content bg-transparent border-0">
                        <div className="modal-body p-0 text-center">
                            <img src={image} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }} />
                            <button className="btn btn-light mt-3" onClick={onClose}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ── Subscription tab helpers ──────────────────────────────────────────────
    const sub = tutorDetail?.active_subscription

    const isExpired = (endDate) => endDate && moment(endDate).isBefore(moment())

    const getDaysRemaining = (endDate) => {
        if (!endDate) return null
        const diff = moment(endDate).diff(moment(), 'days')
        return diff
    }

    const formatValidity = (val) => {
        if (!val) return 'N/A'
        const map = { monthly: 'Monthly', yearly: 'Yearly', 'half-yearly': 'Half Yearly', weekly: 'Weekly' }
        return map[val?.toLowerCase()] || val
    }

    const formatCurrency = (amount, currency) => {
        if (amount === null || amount === undefined) return 'N/A'
        return `${currency || 'AED'} ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    }

    const tabs = [
        { key: 'personal', label: 'Personal Information' },
        { key: 'professional', label: 'Professional Details' },
        { key: 'documents', label: 'Documents' },
        { key: 'subscription', label: 'Subscription' },
    ]

    // ── Subscription tab UI ───────────────────────────────────────────────────
    const SubscriptionTab = () => {
        if (!sub) {
            return (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-5 text-center">
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <i className="fe-credit-card" style={{ fontSize: '32px', color: '#adb5bd' }} />
                        </div>
                        <h6 className="text-muted mb-1">No Active Subscription</h6>
                        <p className="text-muted small mb-0">This tutor has not subscribed to any plan yet.</p>
                    </div>
                </div>
            )
        }

        const isIAP = sub.subscription_type === 'iap'
        const expired = isExpired(sub.end_date)
        const daysLeft = getDaysRemaining(sub.end_date)
        const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && daysLeft >= 0

        return (
            <div className="row g-3">

                {/* ── Status banner ────────────────────────────────────────── */}
                <div className="col-12">
                    <div
                        style={{
                            borderRadius: '12px',
                            padding: '20px 24px',
                            background: expired
                                ? 'linear-gradient(135deg, #dc3545, #c82333)'
                                : isExpiringSoon
                                    ? 'linear-gradient(135deg, #fd7e14, #e96b02)'
                                    : 'linear-gradient(135deg, #28a745, #20c997)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className={`fe-${isIAP ? 'smartphone' : 'user-check'}`} style={{ fontSize: '22px' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: '700' }}>
                                    {sub.subscription_plan?.name || sub.product_name || 'Manual Plan'}
                                </div>
                                <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
                                    {isIAP ? 'In-App Purchase' : 'Manually Assigned by Admin'}
                                </div>
                            </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '13px', opacity: 0.85 }}>
                                {expired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Status'}
                            </div>
                            <div style={{ fontSize: '15px', fontWeight: '700', marginTop: '2px' }}>
                                {expired
                                    ? `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`
                                    : daysLeft === 0
                                        ? 'Expires today'
                                        : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Plan details card ─────────────────────────────────────── */}
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fe-package" style={{ fontSize: '16px', color: '#28a745' }} />
                                </div>
                                <h6 className="mb-0 fw-semibold">Plan Details</h6>
                            </div>

                            <div className="row g-3">
                                <div className="col-6">
                                    <p className="text-muted small mb-1">Plan Name</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.subscription_plan?.name || sub.product_name || 'N/A'}
                                    </p>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Plan Type</p>
                                    <span style={{
                                        fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                        background: isIAP ? '#fef3c7' : '#e0f2fe',
                                        color: isIAP ? '#92400e' : '#0369a1',
                                    }}>
                                        {isIAP ? 'IAP' : 'Manual'}
                                    </span>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Validity</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {formatValidity(sub.plan_validity)}
                                    </p>
                                </div>

                                <div className="col-6">
                                    <p className="text-muted small mb-1">Amount Paid</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {formatCurrency(sub.amount_paid, sub.currency)}
                                    </p>
                                </div>

                                {isIAP && (
                                    <>
                                        <div className="col-6">
                                            <p className="text-muted small mb-1">Device</p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <i className={`fe-${sub.device_type === 'ios' ? 'twitter' : sub.device_type === 'android' ? 'smartphone' : 'monitor'}`} style={{ fontSize: '14px', color: '#6c757d' }} />
                                                <p className="mb-0 fw-semibold" style={{ fontSize: '14px', textTransform: 'capitalize' }}>
                                                    {sub.device_type || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <p className="text-muted small mb-1">Auto Renewing</p>
                                            <span style={{
                                                fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                                background: sub.auto_renewing ? '#d1fae5' : '#fee2e2',
                                                color: sub.auto_renewing ? '#065f46' : '#991b1b',
                                            }}>
                                                {sub.auto_renewing ? 'Yes' : 'No'}
                                            </span>
                                        </div>

                                        <div className="col-12">
                                            <p className="text-muted small mb-1">Product ID</p>
                                            <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                {sub.product_id || 'N/A'}
                                            </p>
                                        </div>
                                    </>
                                )}

                                {!isIAP && sub.admin && (
                                    <div className="col-12">
                                        <p className="text-muted small mb-1">Assigned By</p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className="fe-user" style={{ fontSize: '13px', color: '#0369a1' }} />
                                            </div>
                                            <div>
                                                <p className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>Admin #{sub.admin.id}</p>
                                                <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{sub.admin.email}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Timeline card ─────────────────────────────────────────── */}
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-4">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#e8f4fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fe-calendar" style={{ fontSize: '16px', color: '#0d6efd' }} />
                                </div>
                                <h6 className="mb-0 fw-semibold">Subscription Timeline</h6>
                            </div>

                            {/* Timeline */}
                            <div style={{ position: 'relative', paddingLeft: '24px' }}>

                                {/* Vertical line */}
                                <div style={{ position: 'absolute', left: '7px', top: '8px', bottom: '8px', width: '2px', background: '#dee2e6', borderRadius: '2px' }} />

                                {/* Subscribed date */}
                                <div style={{ position: 'relative', marginBottom: '20px' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: '#0d6efd', border: '2px solid #fff', boxShadow: '0 0 0 2px #0d6efd' }} />
                                    <p className="text-muted small mb-1">Subscribed On</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.subscription_date
                                            ? moment(sub.subscription_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                </div>

                                {/* Start date */}
                                <div style={{ position: 'relative', marginBottom: '20px' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: '#28a745', border: '2px solid #fff', boxShadow: '0 0 0 2px #28a745' }} />
                                    <p className="text-muted small mb-1">Start Date</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px' }}>
                                        {sub.start_date
                                            ? moment(sub.start_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                </div>

                                {/* End date */}
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '-21px', top: '3px', width: '10px', height: '10px', borderRadius: '50%', background: expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745', border: '2px solid #fff', boxShadow: `0 0 0 2px ${expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745'}` }} />
                                    <p className="text-muted small mb-1">End Date</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '14px', color: expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : 'inherit' }}>
                                        {sub.end_date
                                            ? moment(sub.end_date).format('DD MMM YYYY, hh:mm A')
                                            : 'N/A'}
                                    </p>
                                    {isExpiringSoon && !expired && (
                                        <span style={{ fontSize: '11px', color: '#fd7e14', fontWeight: '600' }}>
                                            <i className="fe-alert-circle me-1" />
                                            Expiring in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {expired && (
                                        <span style={{ fontSize: '11px', color: '#dc3545', fontWeight: '600' }}>
                                            <i className="fe-x-circle me-1" />
                                            Expired
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Progress bar */}
                            {sub.start_date && sub.end_date && (() => {
                                const total = moment(sub.end_date).diff(moment(sub.start_date), 'seconds')
                                const elapsed = moment().diff(moment(sub.start_date), 'seconds')
                                const pct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)))
                                const barColor = expired ? '#dc3545' : isExpiringSoon ? '#fd7e14' : '#28a745'
                                return (
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span className="text-muted small">Plan usage</span>
                                            <span className="small fw-semibold" style={{ color: barColor }}>{pct}%</span>
                                        </div>
                                        <div style={{ height: '6px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: '10px', transition: 'width 0.4s ease' }} />
                                        </div>
                                    </div>
                                )
                            })()}
                        </div>
                    </div>
                </div>

                {/* ── Subscription ID row ───────────────────────────────────── */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                        <div className="card-body p-3">
                            <div className="row g-2 align-items-center">
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Subscription ID</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace' }}>#{sub.id}</p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Plan ID</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                                        {sub.subscription_plan_id ? `#${sub.subscription_plan_id}` : 'N/A'}
                                    </p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Record Created</p>
                                    <p className="mb-0 fw-semibold" style={{ fontSize: '13px' }}>
                                        {sub.createdAt ? moment(sub.createdAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}
                                    </p>
                                </div>
                                <div className="col-md-3 col-6">
                                    <p className="text-muted small mb-1">Overall Status</p>
                                    <span style={{
                                        fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px',
                                        background: expired ? '#fee2e2' : '#d1fae5',
                                        color: expired ? '#991b1b' : '#065f46',
                                    }}>
                                        {expired ? 'Expired' : 'Active'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        )
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button onClick={() => navigate('/tutors')} className="btn btn-outline-secondary" style={{ width: '36px', height: '36px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>
                        <i className="fe-arrow-left" style={{ fontSize: '16px' }} />
                    </button>
                    <h4 className="mb-0 fw-semibold">Tutor Details</h4>
                </div>
                {hasPermission('tutors', 'edit') && (
                    <Link to="/tutors/edit" state={{ tutorId }} className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '14px', fontWeight: '500', borderRadius: '6px' }}>
                        <i className="fe-edit me-2" style={{ fontSize: '14px' }} />
                        Edit Tutor
                    </Link>
                )}
            </div>

            {/* Profile card */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <img
                                src={tutorDetail.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                alt="Profile"
                                onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                                style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #dee2e6' }}
                            />
                        </div>
                        <div className="col">
                            <h3 className="mb-2 fw-bold">
                                {`${tutorDetail.first_name || ''} ${tutorDetail.last_name || ''}`.trim() || 'N/A'}
                            </h3>
                            <p className="text-muted mb-2"><i className="fe-mail me-2" />{tutorDetail.email || 'N/A'}</p>
                            <p className="text-muted mb-2">
                                <i className="fe-phone me-2" />
                                {tutorDetail.country_code && tutorDetail.phone_number
                                    ? `${tutorDetail.country_code}${tutorDetail.phone_number}` : 'N/A'}
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                {getStatusBadge(tutorDetail.profile_status)}
                                {getGenderBadge(tutorDetail.gender)}
                                <span className={`badge ${tutorDetail.is_active === 1 ? 'bg-success' : 'bg-danger'}`}>
                                    {tutorDetail.is_active === 1 ? 'Active' : 'Inactive'}
                                </span>
                                {tutorDetail.is_premium_verified === 1 && (
                                    <span className="badge bg-warning text-dark"><i className="fe-star me-1" />Premium</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-0">
                    <ul className="nav nav-tabs" style={{ borderBottom: '1px solid #dee2e6' }}>
                        {tabs.map(tab => (
                            <li key={tab.key} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                    style={{
                                        border: 'none',
                                        borderBottom: activeTab === tab.key ? '2px solid #0d6efd' : '2px solid transparent',
                                        color: activeTab === tab.key ? '#0d6efd' : '#6c757d',
                                        fontWeight: '500',
                                        padding: '12px 24px',
                                    }}
                                >
                                    {tab.key === 'subscription' && tutorDetail?.active_subscription && (
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: isExpired(tutorDetail.active_subscription?.end_date) ? '#dc3545' : '#28a745', display: 'inline-block', marginRight: '6px', marginBottom: '1px' }} />
                                    )}
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Tab content */}
            {activeTab === 'personal' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Personal Information</h5>
                        <div className="row g-4">
                            <div className="col-md-6"><label className="text-muted small mb-1">User ID</label><p className="mb-0 fw-semibold">{tutorDetail.id || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Username</label><p className="mb-0 fw-semibold">{tutorDetail.username || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">First Name</label><p className="mb-0 fw-semibold">{tutorDetail.first_name || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Last Name</label><p className="mb-0 fw-semibold">{tutorDetail.last_name || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Email</label><p className="mb-0 fw-semibold">{tutorDetail.email || 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Phone Number</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.country_code && tutorDetail.phone_number ? `${tutorDetail.country_code}${tutorDetail.phone_number}` : 'N/A'}</p>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Date of Birth</label><p className="mb-0 fw-semibold">{tutorDetail.dob ? moment(tutorDetail.dob).format('DD MMM YYYY') : 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Gender</label><p className="mb-0">{getGenderBadge(tutorDetail.gender)}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Emirates ID</label><p className="mb-0 fw-semibold">{tutorDetail.emirates_id || 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Emirate/State</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.emirate?.en_name || 'N/A'}
                                    {tutorDetail.emirate?.ar_name && <span className="text-muted ms-2">({tutorDetail.emirate.ar_name})</span>}
                                </p>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Language Preference</label><p className="mb-0 fw-semibold">{tutorDetail.lang === 'en' ? 'English' : tutorDetail.lang === 'ar' ? 'Arabic' : 'N/A'}</p></div>
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Languages Spoken</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.languages_spoken?.length > 0
                                        ? tutorDetail.languages_spoken.map((lang, i) => <span key={i} className="badge bg-info">{lang}</span>)
                                        : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12"><label className="text-muted small mb-1">Bio</label><p className="mb-0 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>{tutorDetail.bio || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Created Date</label><p className="mb-0 fw-semibold">{tutorDetail.createdAt ? moment(tutorDetail.createdAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Last Updated</label><p className="mb-0 fw-semibold">{tutorDetail.updatedAt ? moment(tutorDetail.updatedAt).local().format('DD MMM YYYY, hh:mm A') : 'N/A'}</p></div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'professional' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Professional Details</h5>
                        <div className="row g-4">
                            <div className="col-md-6"><label className="text-muted small mb-1">Educational Qualification</label><p className="mb-0 fw-semibold">{tutorDetail.educational_qualification || 'N/A'}</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Subjects Taught</label><p className="mb-0 fw-semibold">{tutorDetail.subjects_taught || 'N/A'}</p></div>
                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Subjects</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.subjects?.length > 0 ? tutorDetail.subjects.map(s => <span key={s.id} className="badge bg-primary" style={{ fontSize: '13px', padding: '6px 12px' }}>{s.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Curriculum</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.curriculums?.length > 0 ? tutorDetail.curriculums.map(c => <span key={c.id} className="badge bg-success" style={{ fontSize: '13px', padding: '6px 12px' }}>{c.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Grades</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.grade_details?.length > 0 ? tutorDetail.grade_details.map(g => <span key={g.id} className="badge bg-warning text-dark" style={{ fontSize: '13px', padding: '6px 12px' }}>{g.option_en}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Age Range</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.age_range_details?.length > 0 ? tutorDetail.age_range_details.map(a => <span key={a.id} className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>{a.name}</span>) : <span className="text-muted">N/A</span>}
                                </div>
                            </div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Commission Percentage</label><p className="mb-0 fw-semibold">{tutorDetail.commission_percentage || 0}%</p></div>
                            <div className="col-md-6"><label className="text-muted small mb-1">Profile Step</label><p className="mb-0 fw-semibold">{tutorDetail.step || 0}</p></div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'documents' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Documents</h5>
                        <div className="row g-4">
                            {[
                                { label: 'Profile Picture', key: 'profile_picture' },
                                { label: 'Emirates ID - Front', key: 'emirates_id_front' },
                                { label: 'Emirates ID - Back', key: 'emirates_id_back' },
                                { label: 'Tutor Permit - Front', key: 'tutor_permit_front' },
                                { label: 'Tutor Permit - Back', key: 'tutor_permit_back' },
                            ].map(({ label, key }) => (
                                <div key={key} className="col-md-6">
                                    <label className="text-muted small mb-2 d-block">{label}</label>
                                    {tutorDetail[key] ? (
                                        <div className="border rounded p-2 text-center" style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }} onClick={() => setSelectedImage(tutorDetail[key])}>
                                            <img src={tutorDetail[key]} alt={label} style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }} />
                                            <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                        </div>
                                    ) : (
                                        <p className="text-muted">Not uploaded</p>
                                    )}
                                </div>
                            ))}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Video Introduction</label>
                                {tutorDetail.video_introduction ? (
                                    <video controls style={{ width: '100%', maxHeight: '200px', borderRadius: '6px' }}>
                                        <source src={tutorDetail.video_introduction} type="video/mp4" />
                                    </video>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'subscription' && <SubscriptionTab />}

            {selectedImage && <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} />}
        </div>
    )
}

export default ViewTutorDetail